import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";

import { EarningV3Api } from "apis/SchemesV3Api";
import { showToast } from "utils";
import { TARGET_FROM_RETAILER_TYPES, EXCLUSION_REASON_LABELS } from "./constants";
import { DataTable, DataRow, Cell, EmptyRow } from "utils/ui";

const STATUS_COLOR = {
  EARNED: { bg: "#ECFDF5", color: "#059669", border: "#D1FAE5" },
  POTENTIAL: { bg: "#FFFBEB", color: "#B45309", border: "#FDE68A" },
  EXCLUDED: { bg: "#F8FAFC", color: "#64748B", border: "#E2E8F0" },
};

function LineStatusBadge({ status }) {
  const s = STATUS_COLOR[status] || STATUS_COLOR.EXCLUDED;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}
    >
      {status === "POTENTIAL" ? "Potential (projected)" : status}
    </span>
  );
}

export default function EarningsTestPanel({ activeSchemes }) {
  const [schemeId, setSchemeId] = useState("");
  const [declaredTarget, setDeclaredTarget] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const selectedScheme = activeSchemes.find((s) => s.id === schemeId);
  const isTargetScheme = (selectedScheme?.configs || []).some((c) =>
    TARGET_FROM_RETAILER_TYPES.includes(c.targetAppliedOn)
  );

  const handleCalculate = () => {
    if (!schemeId) return showToast("Select an ACTIVE scheme first");
    if (isTargetScheme && declaredTarget === "") {
      return showToast("This scheme needs a declared target from the retailer");
    }

    setLoading(true);
    setResult(null);
    EarningV3Api.Calculate(schemeId, isTargetScheme ? declaredTarget : undefined)
      .then((res) => setResult(res.data?.data))
      .catch((err) => showToast(err?.response?.data?.message || "Error calculating earnings"))
      .finally(() => setLoading(false));
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Calculates earnings for the currently logged-in admin/retailer account. With the admin
        account this legitimately returns 0 lines (no inventory) — log in as a retailer with
        inventory on the scheme's products to see real numbers.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Scheme (ACTIVE only)</InputLabel>
            <Select
              label="Scheme (ACTIVE only)"
              value={schemeId}
              onChange={(e) => { setSchemeId(e.target.value); setResult(null); }}
            >
              {activeSchemes.length === 0 && <MenuItem value="" disabled>No ACTIVE schemes found</MenuItem>}
              {activeSchemes.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name}{s.brand?.name ? ` (${s.brand.name})` : ""}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {isTargetScheme && (
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Declared Target"
              value={declaredTarget}
              onChange={(e) => setDeclaredTarget(e.target.value)}
              helperText="Required for *_TARGET_FROM_RETAILER schemes"
            />
          </Grid>
        )}
        <Grid item xs={12} sm={3}>
          <Button fullWidth variant="contained" onClick={handleCalculate} disabled={loading} sx={{ height: 40 }}>
            {loading ? <CircularProgress size={18} color="inherit" /> : "Calculate"}
          </Button>
        </Grid>
      </Grid>

      {result && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <StatCard label="Earned" value={`₹${result.totalEarned ?? 0}`} color="#059669" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard label="Potential" value={`₹${result.totalPotential ?? 0}`} color="#B45309" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard label="Excluded" value={result.excludedCount ?? 0} color="#64748B" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard label="Lines" value={result.lineCount ?? 0} color="#4F46E5" />
            </Grid>
          </Grid>

          {(result.products || []).length === 0 && (
            <Typography color="text.secondary">No product lines returned.</Typography>
          )}

          {(result.products || []).map((product) => (
            <Accordion key={product.productId} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", pr: 2 }}>
                  <Typography sx={{ fontWeight: 600 }}>{product.productName}</Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Chip size="small" label={`Earned ₹${product.earned ?? 0}`} color="success" variant="outlined" />
                    <Chip size="small" label={`Potential ₹${product.potential ?? 0}`} color="warning" variant="outlined" />
                    <Chip size="small" label={`Excluded ${product.excludedCount ?? 0}`} variant="outlined" />
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <DataTable headers={["IMEI", "Txn Basis", "Attribution", "Amount", "Gift", "Status", "Exclusion Reason"]}>
                  {(product.lines || []).length === 0 && <EmptyRow message="No lines" colSpan={7} />}
                  {(product.lines || []).map((line, i) => (
                    <DataRow key={line.imei || i}>
                      <Cell value={line.imei} />
                      <Cell value={line.txnBasis} />
                      <Cell value={line.attribution} />
                      <Cell value={line.gift ? "—" : (line.amount != null ? `₹${line.amount}` : "—")} />
                      <Cell value={line.gift ? <Chip size="small" label={line.gift} color="secondary" /> : "—"} />
                      <Cell><LineStatusBadge status={line.status} /></Cell>
                      <Cell muted value={line.exclusionReason ? (EXCLUSION_REASON_LABELS[line.exclusionReason] || line.exclusionReason) : "—"} />
                    </DataRow>
                  ))}
                </DataTable>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
}

function StatCard({ label, value, color }) {
  return (
    <Box sx={{ p: 2, border: "1px solid #E2E8F0", borderRadius: 1.5, textAlign: "center" }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </Typography>
    </Box>
  );
}
